import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  addFacilityChecklistItem,
  addFacilityComment,
  addPartToRequest,
  addRequestTask,
  deleteFacilityAttachment,
  deleteFacilityChecklistItem,
  deleteFacilityRequest,
  deleteRequestTask,
  downloadAttachment,
  getFacilityRequest,
  listFacilityAssets,
  listFacilityGroups,
  listFacilityParts,
  listFacilityRequests,
  listAssignees,
  listRequestTasks,
  removeRequestPart,
  toggleFacilityChecklistItem,
  toggleFacilityCommentReaction,
  updateFacilityRequest,
  updateRequestTask,
  uploadFacilityAttachment,
} from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { useFavorites } from "../lib/useFavorites";
import { FavoriteStar } from "../components/FavoriteStar";
import { formatBytes, formatDate, formatFacilityNumber, timeAgo } from "../lib/format";
import { useFacilityNumberFormat } from "../lib/facilityNumbering";
import { FacilityConfigProvider, useFacilityOptions } from "../lib/facilityConfig";
import { LocationPicker } from "../components/LocationPicker";
import { FACILITY_RECURRENCES } from "../lib/constants";
import type {
  Assignee,
  FacilityChecklistRow,
  FacilityRequestComment,
  FacilityRequestDetail,
  FacilityRequestPartRow,
  FacilityRequestTaskRow,
  UpdateFacilityRequestInput,
} from "../lib/types";
import { PageBar } from "../components/Layout";
import { confirmDialog } from "../components/ConfirmDialog";
import { AttachmentThumb } from "../components/AttachmentThumb";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";
import { RichTextEditor, RichBody, looksLikeHtml, richTextIsEmpty } from "../components/RichText";
import { AssigneesField, RequestersField, type AssigneePick, type RequesterPick } from "../components/PersonPickers";
import { FacilityStatusBadge, facilityLabel, toDateInput, toUnix } from "./FacilitiesPage";

/** Routed detail page (chrome via PageBar). Reuses the shared body. */
export function FacilityRequestDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  return (
    <>
      <PageBar
        crumbs={
          <>
            <Link to="/facilities">Facilities</Link> <span aria-hidden="true">/</span> Work order
          </>
        }
      />
      <div className="content">
        <FacilityConfigProvider>
          <FacilityRequestDetailBody id={id} onDeleted={() => navigate("/facilities")} />
        </FacilityConfigProvider>
      </div>
    </>
  );
}

/** Self-contained work-order detail body (no PageBar) for the inbox reading pane.
 *  Loads by id and renders properties / procedures / comments / attachments. */
export function FacilityRequestDetailBody({
  id,
  onDeleted,
  onChanged,
}: {
  id: string;
  onDeleted?: () => void;
  onChanged?: () => void;
}) {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApi(
    (signal) => getFacilityRequest(id, signal),
    [id],
  );
  const deleteMut = useMutation(deleteFacilityRequest);
  const numFmt = useFacilityNumberFormat();

  if (loading) return <Loading label="Loading work order…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data) return <EmptyState>Work order not found.</EmptyState>;

  async function onDelete() {
    if (!data) return;
    const yes = await confirmDialog({
      title: "Delete work order",
      message: `Delete work order #${formatFacilityNumber(data.request.number, numFmt)} "${data.request.title}"? This can't be undone.`,
      danger: true,
    });
    if (!yes) return;
    const ok = await deleteMut.run(id);
    if (!ok) return;
    if (onDeleted) onDeleted();
    else navigate("/facilities");
  }

  return (
    <FacilityRequestView
      request={data.request}
      onChanged={() => {
        reload();
        onChanged?.();
      }}
      headerActions={
        <button className="btn danger small" disabled={deleteMut.pending} onClick={onDelete}>
          {deleteMut.pending ? <Spinner label="Deleting…" /> : "Delete"}
        </button>
      }
    />
  );
}

function FacilityRequestView({
  request,
  onChanged,
  headerActions,
}: {
  request: FacilityRequestDetail;
  onChanged: () => void;
  headerActions?: ReactNode;
}) {
  const patchMut = useMutation((patch: UpdateFacilityRequestInput) =>
    updateFacilityRequest(request.id, patch),
  );
  // Dedicated PM-completion mutation: PATCH only { status: "done" } so the backend
  // recognizes the pure status→done transition and clones the next occurrence.
  const completeMut = useMutation(() => updateFacilityRequest(request.id, { status: "done" }));
  const [pmNote, setPmNote] = useState<string | null>(null);
  const numFmt = useFacilityNumberFormat();
  const fc = useFacilityOptions();
  const { isFavorite, toggle: toggleFavorite } = useFavorites();

  const assigneesApi = useApi((signal) => listAssignees(signal, { workType: "work_orders", field: "assignee" }), []);
  const assignees = assigneesApi.data?.assignees ?? [];
  const assetsApi = useApi((signal) => listFacilityAssets({}, signal), []);
  const assets = assetsApi.data?.assets ?? [];
  const groupsApi = useApi((signal) => listFacilityGroups(signal), []);
  const groups = groupsApi.data?.groups ?? [];
  const groupName = groups.find((g) => g.id === request.group_id)?.name ?? null;

  // --- Local edit state for the text/date/people fields. The selects
  // (status/priority/type/recurrence/category/asset) still auto-save on change;
  // everything below is staged here and persisted in ONE top "Save" PATCH. ---
  const [title, setTitle] = useState(request.title);
  const [description, setDescription] = useState(request.description ?? "");
  const [campus, setCampus] = useState(request.campus ?? "");
  const [building, setBuilding] = useState(request.building ?? "");
  const [location, setLocation] = useState(request.location ?? "");
  const [dueDate, setDueDate] = useState(toDateInput(request.due_date));
  const [scheduledFor, setScheduledFor] = useState(toDateInput(request.scheduled_for));
  const [assigneePicks, setAssigneePicks] = useState<AssigneePick[]>(
    request.assignees?.map((a) => ({ user_id: a.user_id, name: a.name })) ?? [],
  );
  const [requesterPicks, setRequesterPicks] = useState<RequesterPick[]>(
    request.requesters?.map((r) => ({ name: r.name, email: r.email })) ?? [],
  );

  // Re-sync local state whenever the saved request changes (reload after save).
  useEffect(() => {
    setTitle(request.title);
    setDescription(request.description ?? "");
    setCampus(request.campus ?? "");
    setBuilding(request.building ?? "");
    setLocation(request.location ?? "");
    setDueDate(toDateInput(request.due_date));
    setScheduledFor(toDateInput(request.scheduled_for));
    setAssigneePicks(request.assignees?.map((a) => ({ user_id: a.user_id, name: a.name })) ?? []);
    setRequesterPicks(request.requesters?.map((r) => ({ name: r.name, email: r.email })) ?? []);
  }, [request]);

  // Each field persists on its own (selects/dates/people on change, text on
  // blur) — no explicit Save. Assignees/requesters go as the full multi-sets.
  async function patch(p: UpdateFacilityRequestInput) {
    const res = await patchMut.run(p);
    if (res) onChanged();
  }

  const isPreventive = request.wo_type === "preventive";
  const isRecurring = !!request.recurrence && request.recurrence !== "none";
  const isDone = request.status === "done";

  async function markComplete() {
    // Capture the current highest work-order number so we can tell whether the
    // backend cloned a follow-up occurrence after the status flip.
    const before = await listFacilityRequests().catch(() => null);
    const maxBefore = before
      ? Math.max(0, ...before.requests.map((r) => r.number))
      : request.number;
    const res = await completeMut.run();
    if (!res) return;
    if (isPreventive && isRecurring) {
      const after = await listFacilityRequests().catch(() => null);
      const maxAfter = after ? Math.max(0, ...after.requests.map((r) => r.number)) : maxBefore;
      setPmNote(
        maxAfter > maxBefore
          ? "Next occurrence scheduled."
          : "Marked complete. (No follow-up occurrence was created.)",
      );
    }
    onChanged();
  }

  const comments = [...request.comments].sort((a, b) => a.created_at - b.created_at);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>
            <span className="mono faint" style={{ fontWeight: 600 }}>#{formatFacilityNumber(request.number, numFmt)}</span>{" "}
            {request.title}
          </h1>
          <div className="sub">
            Created {formatDate(request.created_at)}
            {request.created_by_name ? ` · by ${request.created_by_name}` : ""}
          </div>
          <div className="sub facility-detail-tags">
            <FacilityStatusBadge status={request.status} />{" "}
            <span className="pill">{facilityLabel(fc.woTypes, request.wo_type)}</span>
            {isPreventive && isRecurring ? (
              <span className="pill" title="Preventive maintenance recurrence">
                Recurs {facilityLabel(FACILITY_RECURRENCES, request.recurrence).toLowerCase()}
              </span>
            ) : null}
          </div>
        </div>
        <div className="btn-row">
          <FavoriteStar
            on={isFavorite("facility", request.id)}
            onToggle={() => toggleFavorite("facility", request.id)}
          />
          {!isDone ? (
            <button className="btn primary" disabled={completeMut.pending} onClick={markComplete}>
              {completeMut.pending ? <Spinner label="Completing…" /> : "✓ Mark complete"}
            </button>
          ) : null}
          <button className="btn" onClick={() => window.print()} title="Print this work order">
            🖨 Print
          </button>
          {headerActions}
        </div>
      </div>

      {patchMut.error ? <Alert kind="error">{patchMut.error}</Alert> : null}
      {completeMut.error ? <Alert kind="error">{completeMut.error}</Alert> : null}
      {pmNote ? <Alert kind="success">{pmNote}</Alert> : null}

      <div className="detail-grid">
        <div className="detail-main">
          {/* ----- properties ----- */}
          <div className="card">
            <div className="card-head">
              <h3>Properties</h3>
              <span className="grow" />
              <span className="faint">{patchMut.pending ? "Saving…" : "Saves automatically"}</span>
            </div>
            <div className="card-body">
              <div className="prop-row">
                <label className="field">
                  <span className="label">Status</span>
                  <select
                    value={request.status}
                    onChange={(e) => patch({ status: e.target.value })}
                    disabled={patchMut.pending}
                  >
                    {fc.statuses.map((s) => (
                      <option key={s.code} value={s.code}>
                        {s.label}
                      </option>
                    ))}
                    {!fc.statuses.some((s) => s.code === request.status) ? (
                      <option value={request.status}>{request.status}</option>
                    ) : null}
                  </select>
                </label>
                <label className="field">
                  <span className="label">Priority</span>
                  <select
                    value={request.priority}
                    onChange={(e) => patch({ priority: e.target.value })}
                    disabled={patchMut.pending}
                  >
                    {fc.priorities.map((p) => (
                      <option key={p.code} value={p.code}>
                        {p.label}
                      </option>
                    ))}
                    {!fc.priorities.some((p) => p.code === request.priority) ? (
                      <option value={request.priority}>{request.priority}</option>
                    ) : null}
                  </select>
                </label>
              </div>

              <div className="prop-row">
                <label className="field">
                  <span className="label">Work order type</span>
                  <select
                    value={request.wo_type}
                    onChange={(e) => patch({ wo_type: e.target.value })}
                    disabled={patchMut.pending}
                  >
                    {fc.woTypes.map((t) => (
                      <option key={t.code} value={t.code}>
                        {t.label}
                      </option>
                    ))}
                    {!fc.woTypes.some((t) => t.code === request.wo_type) ? (
                      <option value={request.wo_type}>{request.wo_type}</option>
                    ) : null}
                  </select>
                </label>
                <label className="field">
                  <span className="label">Recurrence</span>
                  <select
                    value={request.recurrence || "none"}
                    onChange={(e) => patch({ recurrence: e.target.value })}
                    disabled={patchMut.pending || !isPreventive}
                  >
                    {FACILITY_RECURRENCES.map((r) => (
                      <option key={r.code} value={r.code}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                  <span className="hint">
                    {isPreventive
                      ? "Preventive + recurring auto-schedules the next occurrence on completion."
                      : "Applies to preventive work orders only."}
                  </span>
                </label>
              </div>

              <label className="field">
                <span className="label">Category</span>
                <select
                  value={request.category ?? ""}
                  onChange={(e) => patch({ category: e.target.value || null })}
                  disabled={patchMut.pending}
                >
                  <option value="">—</option>
                  {fc.categories.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                  {request.category && !fc.categories.some((c) => c.code === request.category) ? (
                    <option value={request.category}>{request.category}</option>
                  ) : null}
                </select>
              </label>

              <label className="field">
                <span className="label">Title</span>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onBlur={() => title.trim() && title.trim() !== request.title.trim() && patch({ title: title.trim() })}
                  required
                />
              </label>
              <label className="field">
                <span className="label">Description</span>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onBlur={() => description.trim() !== (request.description ?? "").trim() && patch({ description: description.trim() || null })}
                  rows={3}
                  style={{ fontFamily: "inherit" }}
                />
              </label>

              <div className="prop-row">
                <LocationPicker
                  campus={campus}
                  building={building}
                  room={location}
                  onChange={({ campus: c, building: b, room: r }) => {
                    setCampus(c);
                    setBuilding(b);
                    setLocation(r);
                    void patch({ campus: c || null, building: b || null, location: r || null });
                  }}
                  disabled={patchMut.pending}
                  layout="stack"
                />
              </div>

              <AssigneesField
                value={assigneePicks}
                options={assignees}
                loading={assigneesApi.loading}
                disabled={patchMut.pending}
                onChange={(next) => {
                  setAssigneePicks(next);
                  void patch({ assignee_ids: next.map((a) => a.user_id) });
                }}
              />

              <label className="field">
                <span className="label">Equipment / asset</span>
                <select
                  value={request.asset_id ?? ""}
                  onChange={(e) => patch({ asset_id: e.target.value || null })}
                  disabled={patchMut.pending || assetsApi.loading}
                >
                  <option value="">—</option>
                  {assets.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.asset_tag ? `${a.asset_tag} · ${a.name}` : a.name}
                    </option>
                  ))}
                  {request.asset_id && !assets.some((a) => a.id === request.asset_id) ? (
                    <option value={request.asset_id}>{request.asset_name ?? request.asset_id}</option>
                  ) : null}
                </select>
              </label>

              <label className="field">
                <span className="label">Group <span className="faint">(controls who can see this work order)</span></span>
                <select
                  value={request.group_id ?? ""}
                  onChange={(e) => patch({ group_id: e.target.value || null })}
                  disabled={patchMut.pending || groupsApi.loading}
                >
                  <option value="">Unassigned (everyone with Facilities access)</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                  {request.group_id && !groups.some((g) => g.id === request.group_id) ? (
                    <option value={request.group_id}>{groupName ?? request.group_id}</option>
                  ) : null}
                </select>
              </label>

              <div className="prop-row">
                <label className="field">
                  <span className="label">Due date</span>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => {
                      setDueDate(e.target.value);
                      void patch({ due_date: e.target.value ? toUnix(e.target.value) : null });
                    }}
                    disabled={patchMut.pending}
                  />
                </label>
                <label className="field">
                  <span className="label">Scheduled date</span>
                  <input
                    type="date"
                    value={scheduledFor}
                    onChange={(e) => {
                      setScheduledFor(e.target.value);
                      void patch({ scheduled_for: e.target.value ? toUnix(e.target.value) : null });
                    }}
                    disabled={patchMut.pending}
                  />
                </label>
              </div>

              <RequestersField
                requesters={requesterPicks}
                disabled={patchMut.pending}
                onChange={(next) => {
                  setRequesterPicks(next);
                  void patch({ requesters: next.map((r) => ({ name: r.name, email: r.email })) });
                }}
              />
            </div>
          </div>

          {/* ----- comments ----- */}
          <div className="card">
            <div className="card-head">
              <h3>Comments</h3>
              <span className="faint">
                {comments.length} comment{comments.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="card-body">
              {comments.length === 0 ? (
                <p className="muted">No comments yet.</p>
              ) : (
                <ul className="thread">
                  {comments.map((c) => (
                    <FacilityCommentItem key={c.id} comment={c} requestId={request.id} onReacted={onChanged} />
                  ))}
                </ul>
              )}
              <FacilityCommentComposer requestId={request.id} onPosted={onChanged} />
            </div>
          </div>
        </div>

        {/* ----- sidebar: tasks + procedures + attachments ----- */}
        <div className="detail-side">
          <FacilityTasksCard requestId={request.id} assignees={assignees} />
          <FacilityChecklistCard
            requestId={request.id}
            items={request.checklist ?? []}
            onChanged={onChanged}
          />
          <FacilityPartsCard
            requestId={request.id}
            parts={request.parts ?? []}
            onChanged={onChanged}
          />
          <FacilityAttachmentsCard request={request} onChanged={onChanged} />
        </div>
      </div>

      <WorkOrderPrintSheet request={request} fc={fc} numFmt={numFmt} comments={comments} />
    </>
  );
}

/**
 * Read-only, print-optimized rendering of a work order. Hidden on screen
 * (display:none) and revealed only by the print stylesheet — mirrors the ticket
 * TicketPrintSheet pattern. Reuses the detail data the page already fetched plus
 * the facility config index (fc) to resolve codes → labels.
 */
function WorkOrderPrintSheet({
  request,
  fc,
  numFmt,
  comments,
}: {
  request: FacilityRequestDetail;
  fc: ReturnType<typeof useFacilityOptions>;
  numFmt: ReturnType<typeof useFacilityNumberFormat>;
  comments: FacilityRequestComment[];
}) {
  const assignees =
    request.assignees && request.assignees.length
      ? request.assignees.map((a) => a.name)
      : request.assignee_name
        ? [request.assignee_name]
        : [];
  const requesters =
    request.requesters && request.requesters.length
      ? request.requesters.map((r) => (r.email ? `${r.name} <${r.email}>` : r.name))
      : request.requester_name
        ? [request.requester_email ? `${request.requester_name} <${request.requester_email}>` : request.requester_name]
        : [];

  const locationParts = [
    facilityLabel(fc.campuses, request.campus),
    facilityLabel(fc.buildings, request.building),
    facilityLabel(fc.rooms, request.location),
  ].filter((p) => p && p !== "—");
  const location = locationParts.length ? locationParts.join(" › ") : "—";

  const checklist = [...(request.checklist ?? [])].sort((a, b) => a.position - b.position);
  const parts = request.parts ?? [];

  const rows: [string, string][] = [
    ["Status", facilityLabel(fc.statuses, request.status)],
    ["Priority", facilityLabel(fc.priorities, request.priority)],
    ["Category", request.category ? facilityLabel(fc.categories, request.category) : "—"],
    ["Work order type", facilityLabel(fc.woTypes, request.wo_type)],
    ["Location", location],
    ["Assignee(s)", assignees.length ? assignees.join(", ") : "Unassigned"],
    ["Requester(s)", requesters.length ? requesters.join(", ") : "—"],
    ["Equipment / asset", request.asset_name ?? "—"],
    ["Due", request.due_date ? formatDate(request.due_date) : "—"],
    ["Scheduled", request.scheduled_for ? formatDate(request.scheduled_for) : "—"],
    ["Opened", formatDate(request.created_at)],
  ];
  if (request.created_by_name) rows.push(["Created by", request.created_by_name]);

  return (
    <div className="wo-print-sheet" aria-hidden="true">
      <header className="tps-head">
        <h1>
          {request.title}{" "}
          <span className="tps-num">#{formatFacilityNumber(request.number, numFmt)}</span>
        </h1>
      </header>

      <table className="tps-props">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}>
              <th>{k}</th>
              <td>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {request.description ? (
        <>
          <h2 className="tps-section">Description</h2>
          {looksLikeHtml(request.description) ? (
            <div className="tps-comment-body"><RichBody html={request.description} /></div>
          ) : (
            <div className="tps-comment-body" style={{ whiteSpace: "pre-wrap" }}>{request.description}</div>
          )}
        </>
      ) : null}

      <h2 className="tps-section">Procedures ({checklist.length})</h2>
      {checklist.length === 0 ? (
        <p>No procedure steps.</p>
      ) : (
        <ul className="wo-print-checklist">
          {checklist.map((c) => (
            <li key={c.id}>
              <span className="wo-print-check" aria-hidden="true">{c.is_done ? "☑" : "☐"}</span>
              <span className={c.is_done ? "wo-print-done" : ""}>{c.label}</span>
            </li>
          ))}
        </ul>
      )}

      <h2 className="tps-section">Parts used ({parts.length})</h2>
      {parts.length === 0 ? (
        <p>No parts logged.</p>
      ) : (
        <ul className="wo-print-parts">
          {parts.map((p) => (
            <li key={p.id}>
              {p.part_name} <span className="tps-num">×{p.quantity}</span>
            </li>
          ))}
        </ul>
      )}

      <h2 className="tps-section">Comments ({comments.length})</h2>
      {comments.length === 0 ? (
        <p>No comments.</p>
      ) : (
        <ol className="tps-comments">
          {comments.map((c) => (
            <li key={c.id} className="tps-comment">
              <div className="tps-comment-meta">
                <strong>{c.author_name ?? "System"}</strong>
                {c.is_internal ? <span className="tps-internal">Internal note</span> : null}
                <span>{formatDate(c.created_at)}</span>
              </div>
              {looksLikeHtml(c.body) ? (
                <div className="tps-comment-body"><RichBody html={c.body} /></div>
              ) : (
                <div className="tps-comment-body" style={{ whiteSpace: "pre-wrap" }}>{c.body}</div>
              )}
            </li>
          ))}
        </ol>
      )}

      <footer className="tps-foot">Printed {formatDate(Math.floor(Date.now() / 1000))}</footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Procedures / checklist card — list + add + toggle + delete via the checklist
// API. Self-contained mutations; reflects done count progress.
// ---------------------------------------------------------------------------

function FacilityChecklistCard({
  requestId,
  items,
  onChanged,
}: {
  requestId: string;
  items: FacilityChecklistRow[];
  onChanged: () => void;
}) {
  const [label, setLabel] = useState("");
  const addMut = useMutation(addFacilityChecklistItem);
  const toggleMut = useMutation(toggleFacilityChecklistItem);
  const delMut = useMutation(deleteFacilityChecklistItem);
  const busy = addMut.pending || toggleMut.pending || delMut.pending;

  const sorted = [...items].sort((a, b) => a.position - b.position);
  const done = sorted.filter((i) => i.is_done).length;

  async function add() {
    const v = label.trim();
    if (!v) return;
    const res = await addMut.run(requestId, v);
    if (res) {
      setLabel("");
      onChanged();
    }
  }

  async function toggle(item: FacilityChecklistRow) {
    const res = await toggleMut.run(requestId, item.id, !item.is_done);
    if (res) onChanged();
  }

  async function remove(item: FacilityChecklistRow) {
    const res = await delMut.run(requestId, item.id);
    if (res) onChanged();
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Procedures</h3>
        <span className="faint">
          {done}/{sorted.length}
        </span>
      </div>
      <div className="card-body">
        {sorted.length === 0 ? (
          <p className="muted">No procedure steps yet.</p>
        ) : (
          <ul className="facility-checklist">
            {sorted.map((item) => (
              <li key={item.id} className={`facility-checklist-item${item.is_done ? " done" : ""}`}>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={!!item.is_done}
                    disabled={busy}
                    onChange={() => toggle(item)}
                  />
                  <span className="facility-checklist-label">{item.label}</span>
                </label>
                <button
                  type="button"
                  className="tab-x"
                  aria-label={`Delete step "${item.label}"`}
                  disabled={busy}
                  onClick={() => remove(item)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="row" style={{ gap: 8, marginTop: 10 }}>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void add();
              }
            }}
            placeholder="Add a procedure step…"
            aria-label="New procedure step"
            disabled={busy}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="btn small"
            disabled={busy || !label.trim()}
            onClick={add}
            style={{ flex: "0 0 auto" }}
          >
            {addMut.pending ? <Spinner label="Adding…" /> : "Add"}
          </button>
        </div>
        {addMut.error ? <Alert kind="error">{addMut.error}</Alert> : null}
        {toggleMut.error ? <Alert kind="error">{toggleMut.error}</Alert> : null}
        {delMut.error ? <Alert kind="error">{delMut.error}</Alert> : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// To-do tasks card (0086) — a richer to-do list than Procedures: each task has
// an assignee, a due date, and a tri-state status that cycles todo →
// in_progress → done on the status chip. Self-contained: fetches + mutates its
// own task list via the per-WO tasks API.
// ---------------------------------------------------------------------------

const TASK_STATUS_CYCLE: Record<string, string> = {
  todo: "in_progress",
  in_progress: "done",
  done: "todo",
};
const TASK_STATUS_LABEL: Record<string, string> = {
  todo: "To do",
  in_progress: "In progress",
  done: "Done",
};

function FacilityTasksCard({ requestId, assignees }: { requestId: string; assignees: Assignee[] }) {
  const tasksApi = useApi((signal) => listRequestTasks(requestId, signal), [requestId]);
  const tasks = tasksApi.data?.tasks ?? [];

  const [title, setTitle] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [due, setDue] = useState("");
  const addMut = useMutation(addRequestTask);
  const updMut = useMutation(updateRequestTask);
  const delMut = useMutation(deleteRequestTask);
  const busy = addMut.pending || updMut.pending || delMut.pending;

  const done = tasks.filter((t) => t.status === "done").length;

  async function add() {
    const v = title.trim();
    if (!v) return;
    const res = await addMut.run(requestId, {
      title: v,
      ...(assigneeId ? { assignee_id: assigneeId } : {}),
      ...(due ? { due_date: toUnix(due) } : {}),
    });
    if (res) {
      setTitle("");
      setAssigneeId("");
      setDue("");
      tasksApi.reload();
    }
  }

  async function cycleStatus(task: FacilityRequestTaskRow) {
    const next = TASK_STATUS_CYCLE[task.status] ?? "todo";
    const res = await updMut.run(task.id, { status: next });
    if (res) tasksApi.reload();
  }

  async function remove(task: FacilityRequestTaskRow) {
    const res = await delMut.run(task.id);
    if (res) tasksApi.reload();
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>To-do tasks</h3>
        <span className="faint">
          {done}/{tasks.length}
        </span>
      </div>
      <div className="card-body">
        {tasksApi.loading ? (
          <Loading />
        ) : tasks.length === 0 ? (
          <p className="muted">No tasks yet.</p>
        ) : (
          <ul className="facility-checklist">
            {tasks.map((task) => (
              <li
                key={task.id}
                className={`facility-checklist-item${task.status === "done" ? " done" : ""}`}
              >
                <button
                  type="button"
                  className={`badge task-status task-status-${task.status}`}
                  disabled={busy}
                  title="Click to advance status"
                  onClick={() => cycleStatus(task)}
                >
                  {TASK_STATUS_LABEL[task.status] ?? task.status}
                </button>
                <span className="facility-checklist-label" style={{ flex: 1 }}>
                  {task.title}
                  <span className="faint" style={{ display: "block", fontSize: "0.85em" }}>
                    {task.assignee_name ?? "Unassigned"}
                    {task.due_date ? ` · due ${formatDate(task.due_date)}` : ""}
                  </span>
                </span>
                <button
                  type="button"
                  className="tab-x"
                  aria-label={`Delete task "${task.title}"`}
                  disabled={busy}
                  onClick={() => remove(task)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="col" style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void add();
              }
            }}
            placeholder="Add a task…"
            aria-label="New task title"
            disabled={busy}
          />
          {/* Assignee on its own full-width row — the date input's wide intrinsic
              size otherwise collapses this select to just its chevron in the
              narrow sidebar. Date + Add share the next row. */}
          <select
            value={assigneeId}
            onChange={(e) => setAssigneeId(e.target.value)}
            aria-label="Task assignee"
            disabled={busy}
            style={{ width: "100%" }}
          >
            <option value="">Unassigned</option>
            {assignees.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <div className="row" style={{ gap: 8 }}>
            <input
              type="date"
              value={due}
              onChange={(e) => setDue(e.target.value)}
              aria-label="Task due date"
              disabled={busy}
              style={{ flex: 1, minWidth: 0 }}
            />
            <button
              type="button"
              className="btn small"
              disabled={busy || !title.trim()}
              onClick={add}
              style={{ flex: "0 0 auto" }}
            >
              {addMut.pending ? <Spinner label="Adding…" /> : "Add"}
            </button>
          </div>
        </div>
        {addMut.error ? <Alert kind="error">{addMut.error}</Alert> : null}
        {updMut.error ? <Alert kind="error">{updMut.error}</Alert> : null}
        {delMut.error ? <Alert kind="error">{delMut.error}</Alert> : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Parts-used card (0040) — lists parts consumed on this work order, with an
// add control (part picker + quantity) and a per-row remove. Adding a part
// decrements catalog stock server-side; removing it restores stock — so the
// detail is reloaded after each change (no client-side inventory math).
// ---------------------------------------------------------------------------

function FacilityPartsCard({
  requestId,
  parts,
  onChanged,
}: {
  requestId: string;
  parts: FacilityRequestPartRow[];
  onChanged: () => void;
}) {
  const partsApi = useApi((signal) => listFacilityParts({}, signal), []);
  const catalog = partsApi.data?.parts ?? [];

  const [partId, setPartId] = useState("");
  const [quantity, setQuantity] = useState("1");

  const addMut = useMutation(addPartToRequest);
  const removeMut = useMutation(removeRequestPart);
  const busy = addMut.pending || removeMut.pending;

  async function add() {
    const qty = Math.round(Number(quantity));
    if (!partId || !Number.isFinite(qty) || qty <= 0) return;
    const res = await addMut.run(requestId, { part_id: partId, quantity: qty });
    if (res) {
      setPartId("");
      setQuantity("1");
      onChanged();
    }
  }

  async function remove(linkId: string) {
    const res = await removeMut.run(requestId, linkId);
    if (res) onChanged();
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Parts used</h3>
        <span className="faint">{parts.length}</span>
      </div>
      <div className="card-body">
        {parts.length === 0 ? (
          <p className="muted">No parts logged yet.</p>
        ) : (
          <ul className="parts-used-list">
            {parts.map((p) => (
              <li key={p.id} className="parts-used-item">
                <span className="parts-used-name">{p.part_name}</span>
                <span className="parts-used-qty mono">×{p.quantity}</span>
                <button
                  type="button"
                  className="tab-x"
                  aria-label={`Remove ${p.part_name}`}
                  disabled={busy}
                  onClick={() => remove(p.id)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="row" style={{ gap: 8, marginTop: 10 }}>
          <select
            value={partId}
            onChange={(e) => setPartId(e.target.value)}
            disabled={busy || partsApi.loading}
            aria-label="Part to add"
            style={{ flex: 1, minWidth: 0 }}
          >
            <option value="">
              {partsApi.loading ? "Loading parts…" : "Select a part…"}
            </option>
            {catalog.map((c) => (
              <option key={c.id} value={c.id}>
                {c.part_number ? `${c.name} (${c.part_number})` : c.name}
                {` — ${c.quantity_on_hand} on hand`}
              </option>
            ))}
          </select>
          <input
            type="number"
            min={1}
            step={1}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            aria-label="Quantity"
            disabled={busy}
            style={{ width: 64, flex: "0 0 auto" }}
          />
          <button
            type="button"
            className="btn small"
            disabled={busy || !partId || !(Number(quantity) > 0)}
            onClick={add}
            style={{ flex: "0 0 auto" }}
          >
            {addMut.pending ? <Spinner label="Adding…" /> : "Add"}
          </button>
        </div>
        <span className="hint">Adding a part consumes it from inventory stock.</span>
        {addMut.error ? <Alert kind="error">{addMut.error}</Alert> : null}
        {removeMut.error ? <Alert kind="error">{removeMut.error}</Alert> : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Attachments card — mirrors the ticket attachments UI (raw-byte upload,
// authenticated blob download, delete). Uses the facility attachment routes.
// ---------------------------------------------------------------------------

function FacilityAttachmentsCard({
  request,
  onChanged,
}: {
  request: FacilityRequestDetail;
  onChanged: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onPick(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      await uploadFacilityAttachment(request.id, file);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  async function onDownload(id: string, filename: string) {
    setError(null);
    try {
      await downloadAttachment(id, filename);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Download failed.");
    }
  }

  async function onRemove(attId: string, filename: string) {
    if (
      !(await confirmDialog({
        title: "Remove attachment",
        message: `Remove attachment "${filename}"? This can't be undone.`,
        danger: true,
        confirmLabel: "Remove",
      }))
    )
      return;
    setError(null);
    try {
      await deleteFacilityAttachment(request.id, attId);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Remove failed.");
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Attachments</h3>
        <label className="btn small" style={{ cursor: uploading ? "wait" : "pointer" }}>
          {uploading ? <Spinner label="Uploading…" /> : "+ Upload"}
          <input type="file" onChange={onPick} disabled={uploading} style={{ display: "none" }} />
        </label>
      </div>
      <div className="card-body">
        {error ? <Alert kind="error">{error}</Alert> : null}
        {request.attachments.length === 0 ? (
          <p className="muted">No attachments yet — upload photos or docs up to 25 MB.</p>
        ) : (
          <ul className="att-list">
            {request.attachments.map((a) => {
              return (
                <li key={a.id} className="att-item">
                  <button
                    type="button"
                    className="att-main"
                    onClick={() => onDownload(a.id, a.filename)}
                    title={`Download ${a.filename}`}
                  >
                    <AttachmentThumb att={a} />
                    <span className="att-info">
                      <span className="att-name">{a.filename}</span>
                      <span className="att-sub">
                        {formatBytes(a.size_bytes)}
                        {a.created_at ? ` · ${timeAgo(a.created_at)}` : ""}
                      </span>
                    </span>
                    <span className="att-dl" aria-hidden="true">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" />
                      </svg>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="att-del"
                    onClick={() => onRemove(a.id, a.filename)}
                    title="Remove attachment"
                    aria-label={`Remove ${a.filename}`}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M6 6l12 12M18 6L6 18" />
                    </svg>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

/** Quick-pick reactions for the work-order comment palette (mirrors TaskComments). */
const FACILITY_COMMENT_EMOJIS = ["👍", "❤️", "🎉", "👀", "✅", "🙏", "😄", "🚀"];

function FacilityCommentItem({
  comment,
  requestId,
  onReacted,
}: {
  comment: FacilityRequestComment;
  requestId: string;
  onReacted: () => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const { run, pending } = useMutation((emoji: string) =>
    toggleFacilityCommentReaction(requestId, comment.id, emoji),
  );

  async function react(emoji: string) {
    setPickerOpen(false);
    const res = await run(emoji);
    if (res) onReacted();
  }

  return (
    <li className={`comment${comment.is_internal ? " internal" : ""}`}>
      <div className="comment-head">
        <strong>{comment.author_name || "System"}</strong>
        {comment.is_internal ? <span className="pill internal-pill">Internal note</span> : null}
        <span className="faint">{timeAgo(comment.created_at)}</span>
      </div>
      <div className="comment-body">
        {looksLikeHtml(comment.body) ? <RichBody html={comment.body} /> : <span style={{ whiteSpace: "pre-wrap" }}>{comment.body}</span>}
      </div>
      <div className="reaction-row">
        {(comment.reactions ?? []).map((r) => (
          <button
            key={r.emoji}
            type="button"
            className={`reaction-chip${r.mine ? " mine" : ""}`}
            disabled={pending}
            onClick={() => react(r.emoji)}
            title={r.mine ? "Remove your reaction" : "React"}
          >
            <span className="reaction-emoji">{r.emoji}</span> {r.count}
          </button>
        ))}
        <div className="reaction-add">
          <button
            type="button"
            className="reaction-add-btn"
            aria-label="Add reaction"
            disabled={pending}
            onClick={() => setPickerOpen((p) => !p)}
          >
            ☺﹢
          </button>
          {pickerOpen ? (
            <div className="reaction-palette" role="menu">
              {FACILITY_COMMENT_EMOJIS.map((e) => (
                <button key={e} type="button" onClick={() => react(e)} title={`React ${e}`}>
                  {e}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </li>
  );
}

function FacilityCommentComposer({
  requestId,
  onPosted,
}: {
  requestId: string;
  onPosted: () => void;
}) {
  const [body, setBody] = useState("");
  // Email recipients for this comment (mirrors the ticket reply composer):
  // default emails the requester, optionally CCs the assignee(s).
  const [emailRequester, setEmailRequester] = useState(true);
  const [emailAssignee, setEmailAssignee] = useState(false);
  // Internal note = staff-only; the requester is never emailed on these.
  const [internal, setInternal] = useState(false);
  const { run, pending, error } = useMutation(addFacilityComment);
  const empty = richTextIsEmpty(body);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (empty) return;
    const res = await run(requestId, body, {
      notify_requester: internal ? false : emailRequester,
      notify_assignee: emailAssignee,
      is_internal: internal,
    });
    if (res) {
      setBody("");
      setEmailRequester(true);
      setEmailAssignee(false);
      setInternal(false);
      onPosted();
    }
  }

  return (
    <form className={`composer${internal ? " composer-internal" : ""}`} onSubmit={onSubmit}>
      {error ? <Alert kind="error">{error}</Alert> : null}
      <RichTextEditor
        value={body}
        onChange={setBody}
        placeholder={internal ? "Add an internal note (staff only)…" : "Add a comment…"}
        disabled={pending}
      />
      <div className="composer-foot">
        <div className="composer-opts">
          <label className="toggle" title="Internal notes are visible to staff only and never emailed to the requester">
            <input
              type="checkbox"
              checked={internal}
              disabled={pending}
              onChange={(e) => setInternal(e.target.checked)}
            />
            Internal note
          </label>
          <label className="toggle" title="Email the requester(s) this comment">
            <input
              type="checkbox"
              checked={internal ? false : emailRequester}
              disabled={pending || internal}
              onChange={(e) => setEmailRequester(e.target.checked)}
            />
            Email requester
          </label>
          <label className="toggle" title="Email the assigned technician(s) this comment">
            <input
              type="checkbox"
              checked={emailAssignee}
              disabled={pending}
              onChange={(e) => setEmailAssignee(e.target.checked)}
            />
            Email assignee
          </label>
        </div>
        <button className="btn primary" type="submit" disabled={pending || empty}>

          {pending ? <Spinner label="Posting…" /> : "Comment"}
        </button>
      </div>
    </form>
  );
}

